/**
 * Per-URL analysis cost — frontend mirror (bd deepcontext-hcw0z).
 *
 * MIRROR of backend/src/constants/analysisCost.ts. The two workspaces don't
 * share a module, so this file MUST stay in sync with the backend definition:
 * same pricing constants, same token footprint, same derivation. If you change
 * one, change the other. The dashboard "Est. Daily Cost" card reads the
 * backend-computed value; this util drives the per-job inline cost figure in
 * ScheduledJobs.tsx so both sites agree.
 *
 * Previously each site hardcoded a flat $0.0006/URL ($600 per 1M) — a magic
 * number disconnected from real model pricing that overstated cost ~3-4x.
 * Now the rate is DERIVED from the production analysis model's real per-token
 * pricing × the realistic per-URL token footprint, so it auto-tracks pricing.
 *
 * Production analysis model: Vertex/Gemini gemini-2.5-flash-lite:
 *   input  $0.10 / 1M tokens, output $0.40 / 1M tokens.
 * Token footprint (bulk batches ~15-25 URLs/call, content truncated to 400
 * chars): ~130 input tokens/URL, ~200 output tokens/URL.
 * See backend/src/constants/analysisCost.ts for the full derivation.
 */

// Mirror of the gemini-2.5-flash-lite tier in backend getGeminiPricingPerMillion().
export const ANALYSIS_INPUT_COST_PER_MILLION_TOKENS = 0.10;
export const ANALYSIS_OUTPUT_COST_PER_MILLION_TOKENS = 0.40;

// Mirror of the per-URL token footprint (see backend header for derivation).
export const AVG_INPUT_TOKENS_PER_URL = 130;
export const AVG_OUTPUT_TOKENS_PER_URL = 200;

/** Derived per-URL analysis cost in USD. Mirrors backend PER_URL_ANALYSIS_COST. */
export const PER_URL_ANALYSIS_COST =
  (AVG_INPUT_TOKENS_PER_URL / 1_000_000) * ANALYSIS_INPUT_COST_PER_MILLION_TOKENS +
  (AVG_OUTPUT_TOKENS_PER_URL / 1_000_000) * ANALYSIS_OUTPUT_COST_PER_MILLION_TOKENS;

/** Estimate the daily analysis cost in USD for a given daily URL volume. */
export function estimateDailyCost(dailyUrls: number): number {
  return dailyUrls * PER_URL_ANALYSIS_COST;
}
